import { Injectable } from '@angular/core';

import {
    ActivityFilter, ActivityParticipantStatus, ConditionPlugin, DuplicatePlugin, FinalizationPlugin, GroupPlugin,
    IActivity, IActivityList, InitializePlugin, RenderAs, ExportSelectPlugin
} from '@azavista/activity-lib';
import { IActivityWithDependencyId, IDependencyWithResult } from 'src/app/interfaces';
import { IGetActivitiesCartResponse } from 'src/app/shared/interfaces';
import { ICartActivityItem, ICartActivityItemGroup } from './interfaces';
import { AppLoggerService, LogLevel } from 'src/app/shared/app-logger.service';

@Injectable({
    providedIn: 'root'
})
export class ActivitiesCartListService {

    constructor(
        private appLoggerSvc: AppLoggerService,
    ) {}

    convertToCartActivityItemGroups(response: IGetActivitiesCartResponse, dependenciesWithResult: IDependencyWithResult[], registeredInIds: string[]): ICartActivityItemGroup[] {
        const filter = new ActivityFilter([
            new InitializePlugin(),
            new ConditionPlugin(),
            new GroupPlugin(),
            new DuplicatePlugin(),
            new ExportSelectPlugin(),
            new FinalizationPlugin(),
        ]);
        const activitiesClone = this.cloneActivitiesAndConvertToDates(response.activities);

        /* createActivitySelect */
        this.appLoggerSvc.log(LogLevel.log, 'activitiesClone - createActivitySelect', activitiesClone);
        this.appLoggerSvc.log(LogLevel.log, 'response.groups - createActivitySelect', response.groups);
        this.appLoggerSvc.log(LogLevel.log, 'response.conditions - createActivitySelect', response.conditions);
        this.appLoggerSvc.log(LogLevel.log, 'registeredInIds - createActivitySelect', registeredInIds);
        /* createActivitySelect */

        let filteredResult = filter.createActivitySelect(activitiesClone, response.groups, response.conditions, registeredInIds);

        /* createActivitySelect */
        this.appLoggerSvc.log(LogLevel.log, 'filteredResult - createActivitySelect', filteredResult);
        /* createActivitySelect */


        const result: ICartActivityItemGroup[] = [];

        for (const group of filteredResult) {
            const createdGroup: ICartActivityItemGroup = { name: group.name, activityItems: [], shownActivityItems: 0 };
            for (const item of group.activities) {
                this.restoreActivityListItemDatesToStrings(item);
                const cartActivityItem = this.createCartActivityItem(item, dependenciesWithResult);
                if (!cartActivityItem.disabledByDependency) {
                    createdGroup.shownActivityItems++;
                }
                createdGroup.activityItems.push(cartActivityItem);
            }
            result.push(createdGroup);
        }
        return result;
    }

    convertToCartActivityItems(
        response: IGetActivitiesCartResponse, dependenciesWithResult: IDependencyWithResult[], registeredInIds: string[]
    ): ICartActivityItem[] {
        const filter = new ActivityFilter([
            new InitializePlugin(),
            new ConditionPlugin(),
            new GroupPlugin(),
            new DuplicatePlugin(),
            new FinalizationPlugin(),
        ]);
        const activitiesClone = this.cloneActivitiesAndConvertToDates(response.activities);

        /* createActivityList */
        this.appLoggerSvc.log(LogLevel.log, 'activitiesClone - createActivityList', activitiesClone);
        this.appLoggerSvc.log(LogLevel.log, 'response.groups - createActivityList', response.groups);
        this.appLoggerSvc.log(LogLevel.log, 'response.conditions - createActivityList', response.conditions);
        this.appLoggerSvc.log(LogLevel.log, 'registeredInIds - createActivityList', registeredInIds);
        /* createActivityList */

        let filteredResult = filter.createActivityList(activitiesClone, response.groups, response.conditions, registeredInIds);

        /* createActivityList */
        this.appLoggerSvc.log(LogLevel.log, 'filteredResult - createActivityList', filteredResult);
        /* createActivityList */

        const result: ICartActivityItem[] = [];
        for (const activity of filteredResult) {
            this.restoreActivityListItemDatesToStrings(activity);
            const cartActivityItem = this.createCartActivityItem(activity, dependenciesWithResult);
            result.push(cartActivityItem);
        }
        return result;
    }

    getFilteredActivityIdsByDependency(
        activitiesIds: string[], activityItems: ICartActivityItem[], dependenciesWithResult: IDependencyWithResult[]
    ): string[] {
        let result = activitiesIds;
        if (dependenciesWithResult) {
            // Check if some activity must not be registered, because its dependency evaluates to false
            for (const activity of activityItems) {
                if (this.getIsActivityFilteredByDependency(activity.dependency_id, dependenciesWithResult)) {
                    // We must remove this activity from registered activities
                    result = result.filter(x => x !== activity.cartActivity.id);
                }
            }
        }
        return result;
    }

    getRegisteredActivitiesFromLibraryGroupResult(
        activityGroupItems: ICartActivityItemGroup[], dependenciesWithResult: IDependencyWithResult[], addRegisteredTo: string | null
    ): string[] {
        const groupedActivities = activityGroupItems.flatMap(x => x.activityItems);
        let result = groupedActivities
            .filter(x => x.cartActivity.renderas === RenderAs.Registered || x.cartActivity.renderas === RenderAs.WaitingList)
            .map(x => x.cartActivity.id);
        if (addRegisteredTo && !result.includes(addRegisteredTo)) {
            result.push(addRegisteredTo);
        }
        result = this.getFilteredActivityIdsByDependency(result, groupedActivities, dependenciesWithResult);
        return result;
    }

    getRegisteredActivitiesFromLibraryListResult(
        activityListItems: ICartActivityItem[], dependenciesWithResult: IDependencyWithResult[], addRegisteredTo: string | null
    ): string[] {
        let result = activityListItems
            .filter(x => x.cartActivity.renderas === RenderAs.Registered || x.cartActivity.renderas === RenderAs.WaitingList)
            .map(x => x.cartActivity.id);
        if (addRegisteredTo && !result.includes(addRegisteredTo)) {
            result.push(addRegisteredTo);
        }
        if (dependenciesWithResult) {
            // Check if some activity must not be registered, because its dependency evaluates to false
            for (const activity of activityListItems) {
                if (this.getIsActivityFilteredByDependency(activity.dependency_id, dependenciesWithResult)) {
                    // We must remove this activity from registered activities
                    result = result.filter(x => x !== activity.cartActivity.id);
                }
            }
        }
        return result;
    }

    getRegisteredActivitiesFromServerResponse(activities: IActivity[], dependenciesWithResult: IDependencyWithResult[], addRegisteredTo?: string): string[] {
        let registeredIn = activities
            .filter(x =>
                x.activity_participant_status === ActivityParticipantStatus.REGISTERED
                || x.activity_participant_status === ActivityParticipantStatus.WAITING_LIST
            )
            .map(x => x.id);
        if (addRegisteredTo && !registeredIn.includes(addRegisteredTo)) {
            registeredIn.push(addRegisteredTo);
        }
        if (dependenciesWithResult) {
            // Check if some activity must not be registered, because its dependency evaluates to false
            for (const activity of activities as IActivityWithDependencyId[]) {
                if (this.getIsActivityFilteredByDependency(activity.dependency_id, dependenciesWithResult)) {
                    // We must remove this activity from registered activities
                    registeredIn = registeredIn.filter(x => x !== activity.id);
                }
            }
        }
        return registeredIn;
    }

    private getIsActivityFilteredByDependency(activityDependencyId: string, dependenciesWithResult?: IDependencyWithResult[]): boolean {
        // Check if some activity must not be registered, because its dependency evaluates to false
        if (activityDependencyId) {
            const dependencyWithResult = dependenciesWithResult.find(x => x.dependency.id === activityDependencyId);
            if (dependencyWithResult && !dependencyWithResult.result) {
                // The dependency is found and it evaluates to false - the activity must be filtered
                return true;
            }
        }
        return false;
    }

    private restoreActivityListItemDatesToStrings(activity: IActivityList): void {
        // TODO: start and end should be strings - now we need to convert them to Date and then back to strings because
        //       the app does not use Date type and requires strings for other operations
        activity.start = activity.start.toISOString() as any;
        activity.end = activity.end.toISOString() as any;
    }

    private cloneActivitiesAndConvertToDates(activities: IActivity[]): IActivity[] {
        const activitiesClone: IActivity[] = JSON.parse(JSON.stringify(activities));
        // TODO: start and end should be strings - now we need to convert them to Date and then back to strings because
        //       the app does not use Date type and requires strings for other operations
        activitiesClone.forEach(x => {
            x.start = new Date(x.start);
            x.end = new Date(x.end);
        });
        return activitiesClone;
    }

    private createCartActivityItem(activity: IActivityList, dependenciesWithResult: IDependencyWithResult[]): ICartActivityItem {
        const activityAsIActivityWithDependencyId = activity as IActivityWithDependencyId;
        const renderAs = activity.renderas;
        let showUnregisterButton = false;
        let showRegisteredLabel = false;
        let showWaitingListButton = false;
        let showWaitingListLabel = false;
        let showApprovalPendingLabel = false;
        let showNotAvailableButton = false;
        let showRegisterButton = false;
        let showPlacesLeftLabel = false;
        let disabled = false;
        if (renderAs === RenderAs.Registered) {
            showUnregisterButton = true;
            showRegisteredLabel = true;
        } else if (renderAs === RenderAs.WaitingList) {
            showUnregisterButton = true;
            showWaitingListLabel = true;
        } else if (renderAs === RenderAs.Disabled) {
            showNotAvailableButton = true;
            disabled = true;
        } else if (renderAs === RenderAs.Open) {
            showRegisterButton = true;
            if (activity.places_left >= 1) {
                showPlacesLeftLabel = true;
            }
        } else if (renderAs === RenderAs.OpenWaitingList) {
            showWaitingListButton = true;
        } else if (renderAs === RenderAs.ApprovalPending) {
            showUnregisterButton = true;
            showApprovalPendingLabel = true;
        }
        const disabledByDependency = this.getIsActivityFilteredByDependency(
            activityAsIActivityWithDependencyId.dependency_id, dependenciesWithResult || []
        );
        const selected = !disabledByDependency && showUnregisterButton;
        const result: ICartActivityItem = {
            cartActivity: activity,
            selected: selected,
            disabled: disabled,
            dependency_id: activityAsIActivityWithDependencyId.dependency_id,
            disabledByDependency: disabledByDependency,
            showNotAvailableButton: showNotAvailableButton,
            showPlacesLeftLabel: showPlacesLeftLabel,
            showRegisterButton: showRegisterButton,
            showRegisteredLabel: showRegisteredLabel,
            showUnregisterButton: showUnregisterButton,
            showWaitingListButton: showWaitingListButton,
            showWaitingListLabel: showWaitingListLabel,
            showApprovalPendingLabel,
        };
        return result;
    }
}
